import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, ArrowRight, Phone, Mail, Calendar,
  ChevronDown, ChevronUp, Award, Users, Target, Briefcase
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const ServiceTemplate = ({ serviceData }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-r from-wazin-navy via-wazin-navy-700 to-wazin-navy-dark text-white py-20"
        style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}
      >
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-wazin-gold to-wazin-gold-600 rounded-full mb-6">
              {serviceData.icon && <serviceData.icon className="w-10 h-10 text-wazin-navy" />}
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              {i18n.language === 'ar' ? serviceData.nameAr : serviceData.nameEn}
            </h1>
            
            <p className="text-xl text-wazin-gold-100 mb-8 max-w-2xl mx-auto">
              {i18n.language === 'ar' ? serviceData.taglineAr : serviceData.taglineEn}
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                onClick={() => navigate('/book-consultation')}
                className="btn-primary gap-2"
              >
                <Calendar className="w-5 h-5" />
                {i18n.language === 'ar' ? 'احجز استشارة' : 'Book Consultation'}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/contact')}
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 gap-2"
              >
                <Phone className="w-5 h-5" />
                {i18n.language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-16" style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
        
        {/* Overview Section */}
        <section className="mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              {i18n.language === 'ar' ? 'نبذة عن الدائرة' : 'About This Department'}
            </h2>
            
            <div className="prose prose-lg max-w-none text-gray-700">
              {i18n.language === 'ar' ? (
                serviceData.descriptionAr.map((para, index) => (
                  <p key={index} className="mb-4 text-lg leading-relaxed">{para}</p>
                ))
              ) : (
                serviceData.descriptionEn.map((para, index) => (
                  <p key={index} className="mb-4 text-lg leading-relaxed">{para}</p>
                ))
              )}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {i18n.language === 'ar' ? 'الخدمات المقدمة' : 'Our Services'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {i18n.language === 'ar' 
                ? 'نقدم مجموعة شاملة من الخدمات القانونية المتخصصة'
                : 'We offer a comprehensive range of specialized legal services'
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceData.services.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300 border-t-4 border-t-amber-500">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-wazin-gold-100 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-wazin-gold" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {i18n.language === 'ar' ? service.nameAr : service.nameEn}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {i18n.language === 'ar' ? service.descAr : service.descEn}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Specializations */}
        <section className="mb-16 bg-gradient-to-r from-wazin-gold-50 to-wazin-gold-100 rounded-2xl p-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              {i18n.language === 'ar' ? 'مجالات العمل' : 'Areas of Practice'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {serviceData.specializations.map((spec, index) => (
                <div key={index} className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
                  <Target className="w-5 h-5 text-wazin-gold flex-shrink-0" />
                  <span className="text-gray-800 font-medium">
                    {i18n.language === 'ar' ? spec.ar : spec.en}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              {i18n.language === 'ar' ? 'لماذا تختار وازن؟' : 'Why Choose Wazin?'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="text-center">
                <CardContent className="pt-6">
                  <Award className="w-12 h-12 text-wazin-gold mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {i18n.language === 'ar' ? 'خبرة متميزة' : 'Distinguished Expertise'}
                  </h3>
                  <p className="text-gray-600">
                    {i18n.language === 'ar' 
                      ? 'أكثر من 15 عاماً من الخبرة في المجال القانوني'
                      : '15+ years of legal expertise'
                    }
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <Users className="w-12 h-12 text-wazin-gold mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {i18n.language === 'ar' ? 'فريق متخصص' : 'Specialized Team'}
                  </h3>
                  <p className="text-gray-600">
                    {i18n.language === 'ar' 
                      ? 'محامون خبراء متخصصون في كل مجال'
                      : 'Expert lawyers specialized in each field'
                    }
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <Briefcase className="w-12 h-12 text-wazin-gold mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {i18n.language === 'ar' ? 'نتائج مضمونة' : 'Guaranteed Results'}
                  </h3>
                  <p className="text-gray-600">
                    {i18n.language === 'ar' 
                      ? 'معدل نجاح عالٍ في القضايا'
                      : 'High success rate in cases'
                    }
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              {i18n.language === 'ar' ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
            </h2>

            <div className="space-y-4">
              {serviceData.faqs.map((faq, index) => (
                <Card key={index} className="overflow-hidden">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full text-left p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 flex-1">
                        {i18n.language === 'ar' ? faq.questionAr : faq.questionEn}
                      </h3>
                      {openFaq === index ? (
                        <ChevronUp className="w-5 h-5 text-wazin-gold flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-wazin-gold flex-shrink-0" />
                      )}
                    </div>
                  </button>
                  
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">
                        {i18n.language === 'ar' ? faq.answerAr : faq.answerEn}
                      </p>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            {i18n.language === 'ar' 
              ? 'هل أنت مستعد لبدء قضيتك؟'
              : 'Ready to Start Your Case?'
            }
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            {i18n.language === 'ar' 
              ? 'احجز استشارة مجانية مع أحد خبرائنا القانونيين اليوم'
              : 'Book a free consultation with one of our legal experts today'
            }
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              onClick={() => navigate('/book-consultation')}
              className="bg-gradient-to-r from-wazin-gold to-wazin-gold-600 text-wazin-navy px-8 py-6 text-lg hover:shadow-lg transition-all gap-2 font-bold"
            >
              <Calendar className="w-5 h-5" />
              {i18n.language === 'ar' ? 'احجز استشارة مجانية' : 'Book Free Consultation'}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => navigate('/contact')}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-8 py-6 text-lg gap-2"
            >
              <Mail className="w-5 h-5" />
              {i18n.language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </Button>
          </div>
          
          <div className="mt-8 flex items-center justify-center gap-6 text-gray-300">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              <span dir="ltr">+966 11 234 5678</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              <span>info@wazin.law</span>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default ServiceTemplate;
